//
// # Text Labels
//
// Really mostly sub-data about the orientation and justification of text labels.
//

// Local Imports
import { exhaust } from "../errors";
import { Point, Bbox, Rotation } from "../core";
import { schema, ElementKind as Kind } from "../schema";

export type TextFormat = schema.TextFormat;
export const TextFormat = {
  default: (): TextFormat => ({
    loc: Point.origin(),
    height: 10,
    hidden: false,
    ...TextOrientation.default(),
  }),

  fromPartial: (arg: Partial<TextFormat>): TextFormat => {
    return {
      ...TextFormat.default(),
      ...arg,
    };
  },

  clone: (fmt: TextFormat): TextFormat => {
    const { loc, ...rest } = fmt;
    return {
      ...rest,
      loc: Point.clone(loc),
    };
  },

  // Strip the non-`TextFormat` fields out of `arg`, returning only those defined in `TextFormat`.
  strip: (arg: TextFormat): TextFormat => {
    const { loc, height, hidden, alignment, baseline, rotation } = arg;
    return { loc, height, hidden, alignment, baseline, rotation };
  },
};

export type Alignment = schema.Alignment;
export const Alignment = {
  Left: "left",
  Center: "center",
  Right: "right",
  default: () => Alignment.Left,
  opposite: oppositeAlignment,
} as const;

export function oppositeAlignment(align: Alignment): Alignment {
  switch (align) {
    case "left":
      return "right";
    case "center":
      return "center";
    case "right":
      return "left";
    default:
      exhaust(align); // Exhaustiveness check
  }
}

export type Baseline = schema.Baseline;
export const Baseline = {
  Top: "top",
  Middle: "middle",
  Bottom: "bottom",
  default: () => Baseline.Middle,
  opposite: oppositeBaseline,
} as const;

export function oppositeBaseline(b: Baseline): Baseline {
  switch (b) {
    case "top":
      return "bottom";
    case "middle":
      return "middle";
    case "bottom":
      return "top";
    default:
      exhaust(b); // Exhaustiveness check
  }
}

export interface AlignmentBaseline {
  alignment: Alignment;
  baseline: Baseline;
}

export type TextOrientation = schema.TextOrientation;
export const TextOrientation = {
  default(): TextOrientation {
    return {
      alignment: Alignment.default(),
      baseline: Baseline.default(),
      rotation: TextRotation.default(),
    };
  },

  fromPartial: (arg: Partial<TextOrientation>): TextOrientation => {
    return {
      ...TextOrientation.default(),
      ...arg,
    };
  },

  clone: (o: TextOrientation): TextOrientation => {
    return { ...o };
  },
};

export type TextRotation = schema.TextRotation;
export const TextRotation = {
  R0: 0,
  R90: 90,
  other: otherTextRotation,
  default: () => TextRotation.R0,
} as const;

export function otherTextRotation(tr: TextRotation): TextRotation {
  return tr === 0 ? 90 : 0;
}

export type Text = schema.Text;
export const Text = {
  Alignment,
  Baseline,
  Orientation: TextOrientation,
  Format: TextFormat,

  new: (arg: Omit<Text, "kind">): Text => ({
    ...arg,
    kind: Kind.Text,
  }),

  default: (): Text => ({
    ...TextFormat.default(),
    text: "",
    kind: Kind.Text,
  }),

  fromPartial: (partial: Partial<Text>): Text => ({
    ...Text.default(),
    ...partial,
  }),

  bbox: (text: Text): Bbox => {
    const { height } = text;
    const { length } = text.text; // Length in characters
    let size = {
      width: length * 0.75 * height, // FIXME: this is a total SWAG!
      height,
    };
    // Start by assuming that the location is (center, middle), and adjust from there.
    // Copy loc so the adjustments below don't mutate the input text.
    const center = { ...text.loc };
    const { alignment, baseline, rotation } = text;
    // If rotated, swap the width and height.
    const rotated = rotation === Rotation.R90;
    if (rotated) {
      size = { width: size.height, height: size.width };
    }
    switch (alignment) {
      case "left":
        if (!rotated) {
          center.x += size.width / 2;
        } else {
          center.y += size.width / 2;
        }
        break;
      case "right":
        if (!rotated) {
          center.x -= size.width / 2;
        } else {
          center.y -= size.width / 2;
        }
        break;
      case "center":
        break;
      default:
        exhaust(alignment);
    }
    switch (baseline) {
      case "top":
        if (!rotated) {
          center.y -= size.height / 2;
        } else {
          center.x -= size.height / 2;
        }
        break;
      case "bottom":
        if (!rotated) {
          center.y += size.height / 2;
        } else {
          center.x += size.height / 2;
        }
        break;
      case "middle":
        break;
      default:
        exhaust(baseline);
    }

    return Bbox.fromSizeAndCenter(size, center);
  },
};
